import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import { changeRequestsApi, confirmationImagesApi } from '../../api/acceptanceApi';
import { featureApi } from '../../api/endpoints';
import { getApiErrorMessage, resolveConfirmationImageUrl } from '../../api/client';
import { notify } from '../../components/Notifier';
import { TicketCodeBadge } from '../../components/TicketCodeBadge';
import { SavedImageGrid } from '../../components/SavedImageGrid';
import { Can } from '../../auth/Can';
import { CHANGE_REQUEST_STATUS_META } from './statusMeta';
import { formatDateTime } from '../../utils/date';
import type {
  ChangeRequest,
  ChangeRequestStatus,
  Feature,
  FeatureConfirmationImage,
} from '../../types';

interface ChangeRequestDetailProps {
  changeRequestId: string;
  onClose: () => void;
  /** Gọi lại khi trạng thái CR được nhân viên cập nhật (để danh sách refresh). */
  onUpdated?: () => void;
}

/** Dialog chi tiết một Change Request: nội dung, lịch sử thread, hành động xử lý. */
export function ChangeRequestDetail({
  changeRequestId,
  onClose,
  onUpdated,
}: ChangeRequestDetailProps): JSX.Element {
  const navigate = useNavigate();
  const [request, setRequest] = useState<ChangeRequest | null>(null);
  const [history, setHistory] = useState<ChangeRequest[]>([]);
  const [feature, setFeature] = useState<Feature | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [resolutionNote, setResolutionNote] = useState('');
  const [updating, setUpdating] = useState<ChangeRequestStatus | null>(null);
  const [images, setImages] = useState<FeatureConfirmationImage[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const detail = await changeRequestsApi.get(changeRequestId);
      setRequest(detail.request);
      setHistory(detail.history);
      try {
        const f = await featureApi.get(detail.request.feature_id);
        setFeature(f);
      } catch {
        setFeature(null);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Không thể tải chi tiết yêu cầu thay đổi.'));
    } finally {
      setLoading(false);
    }
  }, [changeRequestId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Ảnh khách đính lúc gửi yêu cầu nằm ở mốc nghiệm thu sinh cùng bản ghi GỐC
   * của luồng (thread_id === id). Các bản ghi cập nhật trạng thái sau đó có
   * confirmation_id NULL, nên không thể lấy từ `request` (bản mới nhất).
   */
  const rootConfirmationId = useMemo(() => {
    const root = history.find((h) => h.id === h.thread_id);
    return root?.confirmation_id ?? (request?.id === request?.thread_id ? request?.confirmation_id : null);
  }, [history, request]);

  useEffect(() => {
    if (!rootConfirmationId) {
      setImages([]);
      return;
    }
    let cancelled = false;
    confirmationImagesApi
      .list(rootConfirmationId)
      .then((items) => {
        if (!cancelled) setImages(items);
      })
      .catch(() => {
        if (!cancelled) setImages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [rootConfirmationId]);

  const meta = request ? CHANGE_REQUEST_STATUS_META[request.status] : null;

  const handleUpdate = async (status: ChangeRequestStatus) => {
    setUpdating(status);
    try {
      await changeRequestsApi.update(changeRequestId, {
        status,
        note: resolutionNote.trim() || undefined,
      });
      notify('Đã cập nhật trạng thái yêu cầu thay đổi', 'success');
      setResolutionNote('');
      await load();
      onUpdated?.();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Cập nhật thất bại, vui lòng thử lại'), 'error');
    } finally {
      setUpdating(null);
    }
  };

  const goToAcceptance = () => {
    if (!request || !feature) return;
    // KHÔNG gọi onClose() ở đây — cùng lý do với BugDetailDialog.goToFeature:
    // điều hướng rời /change-requests đã tự unmount dialog rồi; gọi thêm
    // onClose() chạy đua với onClose của ChangeRequestList (giờ tự
    // `navigate('/change-requests')` cho cơ chế deep-link mã ticket), khiến
    // "Mở trong màn hình nghiệm thu" bị kéo ngược lại /change-requests.
    navigate(`/projects/${request.project_id}?phaseId=${feature.phase_id}&featureId=${feature.id}`);
  };

  const orderedHistory = useMemo(
    () => [...history].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [history],
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>
          Chi tiết yêu cầu thay đổi
        </Typography>
        <IconButton onClick={onClose} aria-label="Đóng">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error || !request ? (
          <Alert severity="error">{error ?? 'Không tìm thấy yêu cầu.'}</Alert>
        ) : (
          <Stack spacing={2}>
            <Box>
              <TicketCodeBadge
                ticketCode={request.ticket_code}
                path={`/change-requests/${request.ticket_code ?? request.id}`}
              />
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mt: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {request.title}
                </Typography>
                {meta && (
                  <Chip
                    label={meta.label}
                    color={meta.color === 'default' ? 'default' : meta.color}
                    variant={meta.color === 'default' ? 'outlined' : 'filled'}
                    size="small"
                  />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Tính năng: {feature?.name ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tạo lúc {formatDateTime(request.created_at)}
              </Typography>
            </Box>

            {request.description && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {request.description}
              </Typography>
            )}

            {images.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Ảnh khách đính kèm ({images.length})
                </Typography>
                {/* Chỉ xem: ảnh thuộc lịch sử nghiệm thu (append-only), gỡ ảnh
                    chỉ làm được ở màn nghiệm thu và chỉ Admin. */}
                <SavedImageGrid images={images} resolveUrl={resolveConfirmationImageUrl} />
              </Box>
            )}

            <Button
              size="small"
              startIcon={<LaunchIcon />}
              onClick={goToAcceptance}
              disabled={!feature}
              sx={{ alignSelf: 'flex-start' }}
            >
              Mở trong màn hình nghiệm thu
            </Button>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Lịch sử xử lý
              </Typography>
              {orderedHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Chưa có cập nhật nào khác.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {orderedHistory.map((h) => {
                    const hMeta = CHANGE_REQUEST_STATUS_META[h.status];
                    return (
                      <ListItem key={h.id} disableGutters sx={{ alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={hMeta.label}
                                color={hMeta.color === 'default' ? 'default' : hMeta.color}
                                size="small"
                                variant={hMeta.color === 'default' ? 'outlined' : 'filled'}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatDateTime(h.created_at)}
                                {h.creator_name ? ` · ${h.creator_name}` : ''}
                              </Typography>
                            </Stack>
                          }
                          secondary={h.note || undefined}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>

            <Can permission="CHANGED_REQUEST_UPDATE">
              {request.status !== 'RESOLVED' && request.status !== 'REJECTED' && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      Cập nhật xử lý
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      label="Ghi chú xử lý (tuỳ chọn)"
                      value={resolutionNote}
                      onChange={(e) => setResolutionNote(e.target.value)}
                      sx={{ mb: 1.5 }}
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {request.status === 'OPEN' && (
                        <Button
                          variant="outlined"
                          disabled={updating !== null}
                          onClick={() => void handleUpdate('IN_PROGRESS')}
                        >
                          {updating === 'IN_PROGRESS' ? 'Đang lưu…' : 'Tiếp nhận xử lý'}
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        color="success"
                        disabled={updating !== null}
                        onClick={() => void handleUpdate('RESOLVED')}
                      >
                        {updating === 'RESOLVED' ? 'Đang lưu…' : 'Đã xử lý'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={updating !== null}
                        onClick={() => void handleUpdate('REJECTED')}
                      >
                        {updating === 'REJECTED' ? 'Đang lưu…' : 'Từ chối'}
                      </Button>
                    </Stack>
                  </Box>
                </>
              )}
            </Can>

            {(request.status === 'RESOLVED' || request.status === 'REJECTED') && request.note && (
              <Alert severity={request.status === 'RESOLVED' ? 'success' : 'error'}>
                <Typography variant="caption" fontWeight={700} display="block">
                  Kết quả xử lý
                </Typography>
                {request.note}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
      </DialogActions>
    </Dialog>
  );
}
